import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { getWeeklyInsightsHistory, getResultsSnapshot } from "../../lib/weekly";
import { InsightSections, SatisfactionForm, formatWeekOf } from "./WeeklyInsights";

// Stage 2E — read-only history views. Never regenerate; always the saved record.

function Empty({ text, testid }) {
  return <section className="surface-card mt-6 p-8 text-center text-brand-ink-muted" data-testid={testid}>{text}</section>;
}

export function InsightsHistory() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    getWeeklyInsightsHistory().then((d) => setItems(d.items)).catch(() => setItems([]));
  }, []);

  if (items === null) return <Loader2 className="text-brand-primary animate-spin mt-8" size={26} />;
  if (items.length === 0) return <Empty text="Your Weekly Insights will appear here after your first completed check-in." testid="weekly-history-empty" />;

  return (
    <div className="mt-6 space-y-3" data-testid="weekly-history-list">
      {items.map((it) => {
        const isOpen = open === it.submission_id;
        const ins = it.insights || {};
        const done = ins.status === "completed";
        return (
          <div key={it.submission_id} className="surface-card overflow-hidden" data-testid={`weekly-history-item-${it.weekly_window_identifier}`}>
            <button type="button" onClick={() => setOpen(isOpen ? null : it.submission_id)}
              className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left" data-testid={`weekly-history-toggle-${it.weekly_window_identifier}`}>
              <div>
                <div className="font-serif text-2xl text-brand-ink" data-testid="weekly-history-week">{formatWeekOf(it.weekly_window_identifier)}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-brand-ink-muted mt-1">Weekly Insights · {done ? "Completed" : "Being prepared"}</div>
              </div>
              {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {isOpen && (
              <div className="px-5 md:px-6 pb-6 space-y-6" data-testid={`weekly-history-detail-${it.weekly_window_identifier}`}>
                {done ? (
                  <>
                    <InsightSections rec={ins} testidPrefix="weekly-history" />
                    {!ins.satisfaction_submitted && (
                      <SatisfactionForm submissionId={it.submission_id}
                        onRated={() => setItems((arr) => arr.map((x) => x.submission_id === it.submission_id ? { ...x, insights: { ...x.insights, satisfaction_submitted: true } } : x))} />
                    )}
                  </>
                ) : (
                  <p className="text-brand-ink-muted">Your personalised Weekly Insights for this week are being prepared.</p>
                )}
                <button type="button" onClick={() => setOpen(null)} className="btn-ghost text-sm underline" data-testid="weekly-history-close">Back to history</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function ResultsSnapshot() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    getResultsSnapshot().then((d) => setItems(d.items)).catch(() => setItems([]));
  }, []);

  if (items === null) return <Loader2 className="text-brand-primary animate-spin mt-8" size={26} />;
  if (items.length === 0) return <Empty text="Your Results Snapshot builds from Question 8 of each Weekly Check-In. Complete your first check-in to begin." testid="weekly-snapshot-empty" />;

  return (
    <div className="mt-6 space-y-4" data-testid="weekly-snapshot-list">
      {items.map((it) => (
        <article key={it.submission_id} className="surface-card p-6 md:p-8" data-testid={`weekly-snapshot-item-${it.weekly_window_identifier}`}>
          <div className="font-serif text-2xl text-brand-ink">{formatWeekOf(it.weekly_window_identifier)}</div>
          <dl className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[["Physical Win", it.physical_win, "physical-win"], ["Habit Win", it.habit_win, "habit-win"], ["What this showed me about the woman I'm becoming", it.identity_reflection, "identity-reflection"]].map(([label, val, key]) => (
              <div key={key}>
                <dt className="label-eyebrow text-brand-ink">{label}</dt>
                <div className="mt-3 mb-4 h-px w-14 bg-[#C8965A]" aria-hidden data-testid={`weekly-snapshot-${key}-divider`} />
                <dd className={`text-brand-ink leading-relaxed whitespace-pre-line ${key === "identity-reflection" ? "italic" : ""}`} data-testid={`weekly-snapshot-${key}`}>{val}</dd>
              </div>
            ))}
          </dl>
        </article>
      ))}
    </div>
  );
}
